'use client';

import { useEffect, useState } from 'react';

import {
  getPostLikeStatus,
  togglePostLike
} from '@/app/actions/blog-interactions';
import { useToast } from '@/components/ui/Toasts/use-toast';
import { Button } from '@/components/ui/button';

import { Heart } from 'lucide-react';

interface FavoriteButtonProps {
  postSlug: string;
  size?: 'sm' | 'lg';
  variant?: 'default' | 'outline' | 'ghost';
}

export function FavoriteButton({
  postSlug,
  size = 'sm',
  variant = 'ghost'
}: FavoriteButtonProps) {
  const { toast } = useToast();
  const [liked, setLiked] = useState(false);
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchStatus = async () => {
      const status = await getPostLikeStatus(postSlug);
      setLiked(status.liked);
      setCount(status.count);
    };
    fetchStatus();
  }, [postSlug]);

  const handleToggle = async () => {
    setIsLoading(true);
    // Optimistic update
    const prevLiked = liked;
    const prevCount = count;
    setLiked(!prevLiked);
    setCount(prevLiked ? prevCount - 1 : prevCount + 1);

    try {
      const result = await togglePostLike(postSlug);
      if (!result.success) {
        // Rollback if failed
        setLiked(prevLiked);
        setCount(prevCount);
        toast.error(result.error || 'Failed to update preference');
      } else {
        setLiked(result.liked || false);
        if (result.liked) {
          toast.success('Added to favorites', {
            description: 'This article is now in your favorite list.'
          });
        }
      }
    } catch (error) {
      setLiked(prevLiked);
      setCount(prevCount);
      toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleToggle}
      disabled={isLoading}
      className={`gap-2 ${liked ? 'text-red-500 hover:text-red-600' : 'text-muted-foreground hover:text-foreground'}`}>
      <Heart className={`h-4 w-4 ${liked ? 'fill-current' : ''}`} />
      <span>{count > 0 ? count : ''}</span>
      <span className='sr-only'>Like post</span>
    </Button>
  );
}
